import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Gomb, Mezo, Parbeszed } from '../ui/elemek.tsx';
import { hibajegyStatusz } from './api.ts';
import { HibajegyHiba } from './Cimkek.tsx';
import { atmenetHiba, engedettAtmenetek, HIBAJEGY_STATUSZ_CIMKE, type HibajegyStatusz } from './tipusok.ts';

interface Beallitas { gomb: string; cim: string; szoveg: string; megjegyzes: 'nincs' | 'nem_kotelezo' | 'kotelezo'; megjegyzesCimke?: string; valtozat: 'elsodleges' | 'masodlagos' | 'veszely' }

const BEALLITAS: Record<Exclude<HibajegyStatusz, 'bekuldve'>, Beallitas> = {
  javitas_alatt: { gomb: 'Javítás alatt', cim: 'Javítás alatt', szoveg: 'A beküldő értesítést kap, hogy dolgozunk a hibán.', megjegyzes: 'nincs', valtozat: 'masodlagos' },
  kesz: { gomb: 'Kész', cim: 'Hiba javítva', szoveg: 'A jelentés lezárul. A beküldő és az irodavezető levelet kap.', megjegyzes: 'nem_kotelezo', megjegyzesCimke: 'Megjegyzés (nem kötelező)', valtozat: 'elsodleges' },
  elutasitva: { gomb: 'Elutasítás', cim: 'Jelentés elutasítása', szoveg: 'A jelentés lezárul. A beküldő megkapja az indoklást.', megjegyzes: 'kotelezo', megjegyzesCimke: 'Indoklás', valtozat: 'veszely' },
  uj_fejlesztes: { gomb: 'Új fejlesztés', cim: 'Új fejlesztés', szoveg: 'A beküldő és az irodavezető levelet kap, hogy ez díjköteles új fejlesztés.', megjegyzes: 'nincs', valtozat: 'masodlagos' },
};

/** A super admin léptető gombjai: csak az aktuális állapotból engedett átmenetek látszanak. */
export default function Leptetes({ hibajegyId, statusz }: { hibajegyId: string; statusz: HibajegyStatusz }) {
  const kliens = useQueryClient();
  const [cel, setCel] = useState<Exclude<HibajegyStatusz, 'bekuldve'> | null>(null);
  const [megjegyzes, setMegjegyzes] = useState('');
  const [mezoHiba, setMezoHiba] = useState<string | null>(null);

  const mentes = useMutation({
    mutationFn: (c: { statusz: HibajegyStatusz; megjegyzes: string }) => hibajegyStatusz(hibajegyId, c.statusz, c.megjegyzes),
    onSuccess: async () => {
      setCel(null);
      setMegjegyzes('');
      await kliens.invalidateQueries({ queryKey: ['hibajegyek'] });
    },
  });

  const atmenetek = engedettAtmenetek(statusz) as Exclude<HibajegyStatusz, 'bekuldve'>[];
  if (atmenetek.length === 0) return null;
  const b = cel ? BEALLITAS[cel] : null;

  function nyit(c: Exclude<HibajegyStatusz, 'bekuldve'>) {
    setMezoHiba(null);
    mentes.reset();
    setMegjegyzes('');
    // A „Javítás alatt” egy kattintás, megerősítés nélkül.
    if (c === 'javitas_alatt') mentes.mutate({ statusz: c, megjegyzes: '' });
    else setCel(c);
  }
  function bezar() { setCel(null); setMezoHiba(null); }

  return (
    <>
      <div role="group" aria-label="Állapot módosítása" className="flex flex-wrap gap-2">
        {atmenetek.map((c) => (
          <Gomb key={c} valtozat={BEALLITAS[c].valtozat} disabled={mentes.isPending} onClick={() => nyit(c)}>{BEALLITAS[c].gomb}</Gomb>
        ))}
      </div>
      {cel === null && <HibajegyHiba hiba={mentes.error} />}
      <Parbeszed nyitva={cel !== null} cim={b?.cim ?? ''} onBezar={bezar}>
        {b && cel && (
          <form className="grid gap-4" onSubmit={(e) => {
            e.preventDefault();
            const h = atmenetHiba(cel, megjegyzes);
            setMezoHiba(h);
            if (!h) mentes.mutate({ statusz: cel, megjegyzes: b.megjegyzes === 'nincs' ? '' : megjegyzes });
          }}>
            <p className="text-sm text-szoveg-halk">{b.szoveg} Új állapot: <b className="font-medium text-szoveg">{HIBAJEGY_STATUSZ_CIMKE[cel]}</b>.</p>
            {b.megjegyzes !== 'nincs' && (
              <Mezo label={b.megjegyzesCimke ?? 'Megjegyzés'} multiline value={megjegyzes} maxLength={2000} hiba={mezoHiba} onChange={(e) => setMegjegyzes(e.target.value)} />
            )}
            <HibajegyHiba hiba={mentes.error} />
            <div className="flex flex-wrap justify-end gap-3">
              <Gomb valtozat="masodlagos" onClick={bezar}>Mégsem</Gomb>
              <Gomb type="submit" valtozat={b.valtozat === 'veszely' ? 'veszely' : 'elsodleges'} toltes={mentes.isPending}>Megerősítem</Gomb>
            </div>
          </form>
        )}
      </Parbeszed>
    </>
  );
}
